import { createContext, useContext, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import type { User } from '../../types/api';

interface AuthState { user: User | null; loading: boolean; refresh: () => Promise<void>; logout: () => Promise<void> }
const Ctx = createContext<AuthState>({ user: null, loading: true, refresh: async () => {}, logout: async () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try { return (await api<{ user: User }>('/api/auth/me')).user; }
      catch (e) { if (e instanceof ApiError && e.status === 401) return null; throw e; }
    },
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const value: AuthState = {
    user: q.data ?? null,
    loading: q.isLoading,
    refresh: async () => { await qc.invalidateQueries({ queryKey: ['me'] }); },
    logout: async () => { await api('/api/auth/logout', { method: 'POST', body: {} }); qc.clear(); await q.refetch(); },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
export const useAuth = () => useContext(Ctx);
